import { isObject } from '@mng/mng-ts-helpers';
import {
  ModelItemOptions,
  ReadOnlyFunction,
  RequiredFunction,
  ValueOptions,
  ValueOptionsType
} from './model-item-options';
import { DefaultValueHandler, FieldType, IModelContext } from './model.types';
import { IValidationContext, IValidationResult, ValidationFunction } from './validation/model-validation';
import {
  bicValidation,
  dateValidation,
  ibanValidation,
  maxlengthValidation,
  maxvalueValidation,
  minlengthValidation,
  minvalueValidation,
  requiredValidation,
  timeValidation
} from './validation/validation.helper';

export interface ModelOption<T = any> {
  name: string;
  value: T;
  disabled?: boolean;
}

function resolveParentArrays(group: ModelGroup, arrayGroups: ModelGroup[] = []): ModelGroup[] {
  if (group) {
    if (group.isArray) {
      arrayGroups.push(group);
    }
    if (group.parent) {
      resolveParentArrays(group.parent, arrayGroups);
    }
  }
  return arrayGroups;
}

export interface INumberFieldOptions {
  allowDecimal?: boolean;
  allowNegative?: boolean;
}

export class ModelItem {
  isArray: boolean;
  _parentArrayGroups: ModelGroup[];
  arrayId: string;
  _options: ModelItemOptions;
  _parentGroupsWithDependencies: ModelGroup[];
  private _children: ModelItem[];
  pathPrefix: string;

  constructor(
    public id: string,
    public description?: string,
    public parent?: ModelGroup
  ) {}

  get parentArrayGroups(): ModelGroup[] {
    if (!this._parentArrayGroups) {
      const tempParentArrayGroups = resolveParentArrays(this.parent);
      this._parentArrayGroups =
        tempParentArrayGroups.length > 0 ? tempParentArrayGroups.reverse() : tempParentArrayGroups;
    }
    return this._parentArrayGroups;
  }

  get isArrayBased(): boolean {
    return this.parentArrayGroups.length > 0;
  }

  get parentGroupsWithDependencies(): ModelGroup[] {
    if (!this._parentGroupsWithDependencies) {
      this._parentGroupsWithDependencies = this.getModelGroupHierarchy().filter((modelGroup: ModelGroup) =>
        modelGroup.hasDependencies()
      );
    }
    return this._parentGroupsWithDependencies;
  }

  getItemById(id: string): ModelItem {
    return this.fields.find((field: ModelField) => field.id === id);
  }

  /**
   * @param relativeRoot
   * @returns parent groups als Array beginnend von root
   */
  getModelGroupHierarchy(relativeRoot?: ModelItem): ModelGroup[] {
    if (!this.parent) {
      return [];
    }
    const hierarchyGroups: ModelGroup[] = this instanceof ModelGroup ? [this] : [];
    let currentGroup = this.parent;

    while (currentGroup) {
      hierarchyGroups.push(currentGroup);

      if (relativeRoot && currentGroup === relativeRoot) {
        // wenn relativeRoot gesetzt wird an dieser Stelle beendet
        break;
      }
      currentGroup = currentGroup.parent;
    }

    return hierarchyGroups.reverse();
  }

  get children(): ModelItem[] {
    if (!this._children) {
      this._children = [];
    }
    return this._children;
  }

  get root(): ModelItem {
    return this.parent ? this.parent.root : this;
  }

  setParent(item: ModelGroup): this {
    this.parent = item;
    return this;
  }

  addItem(item: ModelItem): this {
    this.children.push(item);
    return this;
  }

  hasValidations(): boolean {
    return this.options.hasValidations();
  }

  hasDefaultValueHandler(): boolean {
    return !!this.options.defaultValueHandler;
  }

  hasDependencies(): boolean {
    return this.options.hasDependencies();
  }

  hasOptions(): boolean {
    return !!this._options;
  }

  hasValueOptions(): boolean {
    return this.hasOptions() && !!this.options.valueOptions;
  }

  withRemoveDataWhenNotRelevant(value = true): this {
    this.options.removeDataWhenNotRelevant = value;
    return this;
  }

  defaultValue(modelContext: IModelContext): any {
    return this.hasDefaultValueHandler() ? this.defaultValueHandler(this, modelContext) : null;
  }

  isRelevant(modelContext: IModelContext): boolean {
    if (!this.hasDependencies()) {
      return true;
    }

    let result = true;
    let dependency: ModelDependency;
    for (dependency of this.options.dependencies) {
      result = dependency.isRelevant(modelContext);
      if (!result) {
        break;
      }
    }

    return result;
  }

  isNoValidation(): boolean {
    return this.options.noValidation;
  }

  withNoValidation(value = true): this {
    this.options.withNoValidation(value);
    return this;
  }

  get defaultValueHandler(): DefaultValueHandler {
    return this.options.defaultValueHandler;
  }

  get fields(): ModelField[] {
    return this.children.filter((item: ModelItem) => item instanceof ModelField);
  }

  get groups(): ModelGroup[] {
    return this.children.filter((item: ModelItem) => item instanceof ModelGroup);
  }

  /**
   * Rückgabe eines IValidationResult bedeutet dass ein Fehler aufgetreten ist
   *
   * @param data
   * @returns {null}
   */

  validate(modelContext: IModelContext | any): IValidationContext {
    // console.log('validate', 'id', this.realId(), 'path', this.path, value, 'parts', parts );

    if (!isObject(modelContext)) {
      modelContext = { value: modelContext };
    }

    const resultContainer: IValidationContext = {
      modelContext,
      modelItem: this
    };

    if (!this.isRelevant(modelContext)) {
      resultContainer.relevant = false;
      return resultContainer;
    } else {
      resultContainer.relevant = true;
    }

    let validationResult: IValidationResult = null;
    if (this.hasValidations()) {
      let validationFunction: ValidationFunction;
      for (validationFunction of this.options.validations) {
        validationResult = validationFunction(this, modelContext);
        if (validationResult) {
          validationResult.data = modelContext.value;
          // Error indicator
          break;
        }
      }
      resultContainer.result = validationResult;
    }
    return resultContainer;
  }

  get options(): ModelItemOptions {
    if (!this._options) {
      this._options = new ModelItemOptions();
    }
    return this._options;
  }

  set options(value: ModelItemOptions) {
    this._options = value;
  }

  withDependency(value: ModelDependency, isRequired = true): this {
    this.options.addDependency(value);
    if (isRequired) {
      this.withRequired();
    }
    return this;
  }

  withFunctionDependency(func: DependencyFunction, isRequired = true, dependencyItem?: ModelItem): this {
    return this.withDependency(new ModelDependencyByFunction(func, dependencyItem), isRequired);
  }

  withFunctionDependencies(funcs: DependencyFunction[], isRequired = true, dependencyItem?: ModelItem): this {
    funcs.forEach((func) => {
      this.withDependency(new ModelDependencyByFunction(func, dependencyItem), isRequired);
    });
    return this;
  }

  withOptions(value: ModelItemOptions): this {
    this.options = value;
    return this;
  }

  withRequired(value = true): this {
    this.options.withRequired(value);
    return this.withValidation(requiredValidation);
  }

  withRequiredFunction(value: RequiredFunction): this {
    this.options.withRequiredFunction(value);
    return this.withValidation(requiredValidation);
  }

  withReadOnly(value = true): this {
    this.options.withReadOnly(value);
    return this;
  }

  withReadOnlyFunction(value: ReadOnlyFunction): this {
    this.options.withReadOnlyFunction(value);
    return this;
  }

  withDefaultValueHandler(defaultValueHandler: DefaultValueHandler): this {
    this.options.withDefaultValueHandler(defaultValueHandler);
    return this;
  }

  withValidation(validationFunction: ValidationFunction): this {
    this.options.withValidation(validationFunction);
    return this;
  }

  withPathPrefix(value: string): this {
    this.pathPrefix = value;
    return this;
  }

  withMaxlength(value: number): this {
    this.options.maxlength = value;
    return this.withValidation(maxlengthValidation);
  }

  withMinlength(value: number): this {
    this.options.minlength = value;
    return this.withValidation(minlengthValidation);
  }

  withMaxvalue(value: number): this {
    this.options.maxvalue = value;
    return this.withValidation(maxvalueValidation);
  }

  withMinvalue(value: number): this {
    this.options.minvalue = value;
    return this.withValidation(minvalueValidation);
  }

  toString(): string {
    return `${this.path}`;
  }

  realId(): string {
    return this.isArray && this.arrayId ? this.arrayId : this.id;
  }

  get path(): string {
    return this.dynamicPath();
  }

  getDataId(relativeRoot?: ModelGroup): string {
    if (this.isRootGroup(relativeRoot)) {
      return '';
    }
    return this.realId();
  }

  isRootGroup(relativeRoot?: ModelGroup): boolean {
    if (!this.parent) {
      return true;
    }

    if (relativeRoot && this instanceof ModelGroup) {
      if (relativeRoot.realId() === this.realId()) {
        return true;
      }
    }
    return false;
  }

  dynamicPath(relativeRoot?: ModelGroup): string {
    if (this.isRootGroup(relativeRoot)) {
      return '';
    }

    const parentDynamicPath = this.parent.dynamicPath(relativeRoot);

    const parentPath = parentDynamicPath.length > 0 ? `${parentDynamicPath}.` : '';
    const currentPath = `${parentPath}${this.realId()}`;
    return currentPath;
  }

  hasPath(): boolean {
    const currentPath = this.path;
    return currentPath && currentPath.length > 0;
  }

  asArray(): this {
    this.isArray = true;
    return this;
  }
}

export interface IModelGroupImportDef {
  localImport?: boolean; // kein Import notwendig da schon im Model definiert -> z.B. bei der mehrfachen Verwendung von Definitionen im Model an unterschiedlichen Stellen
  importStatement?: string; // voll qualifiziertes import statement (z.B. `import { VermittlerDto } from './antrag.model.types'`)
  importModel?: { modelName: string; interfaceName: string }; // z.B. {modelName: 'antrag', interfaceName: 'VermittlerDto'}
}

export class ModelGroup extends ModelItem {
  interfaceName: string;
  importDef: IModelGroupImportDef; // wenn kein Interface generiert wird sondern ein bestehendens verwendet werden soll

  // Kennzeichen für die Validierung dass für diese Group die Validierung in Ordnung ist
  // wenn die Daten dieser Group null oder undefined sind
  notRequiredWhenEmpty = false;

  constructor(
    id: string,
    public description?: string,
    public parent?: ModelGroup
  ) {
    super(id, description, parent);
  }

  addItems(...item: ModelItem[]): this {
    item.forEach((currentItem: ModelItem) => {
      currentItem.parent = this;
      this.addItem(currentItem);
    });

    return this;
  }

  addArrayGroup(id: string, description?: string, options?: ModelItemOptions): ModelGroup {
    const group = this.addGroup(id, description, options);
    group.isArray = true;
    return group;
  }

  addGroup(id: string, description?: string, options?: ModelItemOptions): ModelGroup {
    const group: ModelGroup = new ModelGroup(id, description, this);
    group.options = options;
    this.addItem(group);
    return group;
  }

  addArrayGroupObject(group: ModelGroup): this {
    group.isArray = true;
    this.addGroupObject(group);
    return this;
  }

  addGroupObject(group: ModelGroup): this {
    group.parent = this;
    this.addItem(group);
    return this;
  }

  addGroupObjects(groups: ModelGroup[]): this {
    for (const group of groups) {
      this.addGroupObject(group);
    }
    return this;
  }

  withInterfaceName(value: string): this {
    this.interfaceName = value;
    return this;
  }

  withNotRequiredWhenEmpty(): this {
    this.notRequiredWhenEmpty = true;
    return this;
  }

  /**
   * Wird nur für Codegenerierung verwendet.
   * Ein Interface wird dann für die Gruppe (und Childgruppen nicht generiert) sonder wird importiert
   *
   * @param value
   */
  withImportDef(value: IModelGroupImportDef): this {
    this.importDef = value;
    return this;
  }
}

export class ModelField extends ModelItem {
  constructor(
    public id: string,
    public description?: string,
    public type?: string,
    public parent?: ModelGroup
  ) {
    super(id, description, parent);
  }

  withValueOptions(value: ValueOptions): this {
    this.options.withValueOptions(value);
    return this;
  }

  withLongListType(value = true): this {
    this.options.withLongListType(value);
    return this;
  }

  withPlaceholder(value: string): this {
    this.options.withPlaceholder(value);
    return this;
  }

  withValueOptionsMany(value: ValueOptions): this {
    this.options.withValueOptionsMany(value);
    return this;
  }

  hasValueOptions(): boolean {
    return this.hasOptions() && !!this.valueOptions;
  }

  hasValueOptionsMany(): boolean {
    return this.hasValueOptions() && this.options.valueOptions.type === ValueOptionsType.ManyOf;
  }

  get valueOptions(): ValueOptions {
    return this.options.valueOptions;
  }
}

export class StringField extends ModelField {
  constructor(
    public id: string,
    public description?: string,
    public parent?: ModelGroup
  ) {
    super(id, description, FieldType.STRING, parent);
  }
}

export class IbanField extends ModelField {
  constructor(
    public id: string,
    public description?: string,
    public parent?: ModelGroup
  ) {
    super(id, description, FieldType.IBAN, parent);
    this.withValidation(ibanValidation);
  }
}

export class BicField extends ModelField {
  constructor(
    public id: string,
    public description?: string,
    public parent?: ModelGroup
  ) {
    super(id, description, FieldType.BIC, parent);
    this.withValidation(bicValidation);
  }
}

export class MultilineField extends ModelField {
  constructor(
    public id: string,
    public description?: string,
    public parent?: ModelGroup
  ) {
    super(id, description, FieldType.MULTILINE, parent);
  }
}

export class BooleanField extends ModelField {
  constructor(
    public id: string,
    public description?: string,
    public parent?: ModelGroup
  ) {
    super(id, description, FieldType.BOOLEAN, parent);
  }
}

export class NumberField extends ModelField {
  numberFieldOptions: INumberFieldOptions;

  constructor(
    public id: string,
    public description?: string,
    public parent?: ModelGroup
  ) {
    super(id, description, FieldType.NUMBER, parent);
  }

  withNumberFieldOptions(options: INumberFieldOptions): this {
    this.numberFieldOptions = options;
    return this;
  }

  hasNumberFieldOptions(): boolean {
    return !!this.numberFieldOptions;
  }

  isDecimalAllowed(): boolean {
    return this.hasNumberFieldOptions() && this.numberFieldOptions.allowDecimal;
  }

  isNegativeAllowed(): boolean {
    return this.hasNumberFieldOptions() && this.numberFieldOptions.allowNegative;
  }
}

export class DateField extends ModelField {
  constructor(
    public id: string,
    public description?: string,
    public parent?: ModelGroup
  ) {
    super(id, description, FieldType.DATE, parent);
    this.withValidation(dateValidation);
  }
}

export class TimeField extends ModelField {
  constructor(
    public id: string,
    public description?: string,
    public parent?: ModelGroup
  ) {
    super(id, description, FieldType.TIME, parent);
    this.withValidation(timeValidation);
  }
}

export class DateTimeField extends ModelField {
  constructor(
    public id: string,
    public description?: string,
    public parent?: ModelGroup
  ) {
    super(id, description, FieldType.DATE_TIME, parent);
  }
}

export class UnmanagedField extends ModelField {
  constructor(
    public id: string,
    public description?: string,
    public parent?: ModelGroup
  ) {
    super(id, description, 'any', parent);
  }
}

export interface IModelDependencyStrategy {
  isRelevant(dependency: ModelDependency, modelContext: IModelContext): boolean;
}

export type DependencyFunction = (dependency: ModelDependency, modelContext: IModelContext) => boolean;

export class ModelDependencyStrategyByFunction implements IModelDependencyStrategy {
  constructor(public func: DependencyFunction) {}

  isRelevant(dependency: ModelDependency, modelContext: IModelContext): boolean {
    return this.func(dependency, modelContext);
  }
}

export class ModelDependency {
  constructor(
    public strategy: IModelDependencyStrategy,
    public item?: ModelItem
  ) {}

  isRelevant(modelContext: IModelContext): boolean {
    return this.strategy.isRelevant(this, modelContext);
  }
}

export class ModelDependencyByFunction extends ModelDependency {
  constructor(
    public func: DependencyFunction,
    public item?: ModelItem
  ) {
    super(new ModelDependencyStrategyByFunction(func), item);
  }
}
